import type { CarbonIconType } from "@carbon/icons-react";
import type { TFunction } from "i18next";
import type { RouteObject } from "react-router";
import type { AttributeReferenceObjectType } from "src/schemas/attributeSchemas";
import type { EntityCrumb } from "src/utils/breadcrumbUtils";
import type { ExtensionKey } from "src/utils/extensionUtils";
import { ACCESS_RULES, type AccessRule } from "src/utils/permissionUtils";
import { lazyRoute } from "./lazyRoutes";

type LazyRoute = ReturnType<typeof lazyRoute>;

type EntityLabelHook = EntityCrumb["useLabel"];

/**
 * Imports the hook that names an entity crumb. Deferred rather than imported
 * here: a section's hooks reach its service and schemas, and pulling every
 * section's into the route table would put all of them in the entry chunk.
 */
type EntityLabelImport = () => Promise<EntityLabelHook>;

/** Supplies the crumb through the route's own chunk. */
export const lazyEntityCrumb =
  (importLabel: EntityLabelImport) => async () => ({
    handle: { crumb: { useLabel: await importLabel() } },
  });

interface EntitySectionOptions {
  /** Absolute path of the section, e.g. `/data/resources`. */
  path: string;
  icon: CarbonIconType;
  label: (t: TFunction) => string;
  list: LazyRoute;
  details?: LazyRoute;
  form?: LazyRoute;
  /** Names the `:id` crumb every page below the section inherits. */
  entityLabel: EntityLabelImport;
  /** Names the entity segment where the section does not call it `:id`. */
  idParam?: string;
  /** Sections whose entities the platform creates, never the user. */
  noAddPage?: boolean;
  access?: { read?: AccessRule; write?: AccessRule };
  /** Extension this whole section belongs to; every page below inherits it. */
  configFlag?: ExtensionKey;
  /** Hangs the shared attribute-value pages off the entity for this parent. */
  attributeValues?: AttributeReferenceObjectType;
  /** Write rule for those pages, where the section owns them itself. */
  attributeValuesWrite?: AccessRule;
  /** Extra pages below `:id`, in the shape the section needs them. */
  entityChildren?: RouteObject[];
  /** Extra pages beside the list, such as the alert list's variants. */
  sectionChildren?: RouteObject[];
}

export const attributeValueRoutes = (
  parentType: AttributeReferenceObjectType,
  write: AccessRule = ACCESS_RULES.dataDefinitionsWrite,
): RouteObject[] => [
  {
    path: "attribute-values/add",
    lazy: lazyRoute(() => import("src/subEntities/AttributeValueForm"), {
      parentType,
    }),
    handle: {
      access: write,
      crumb: {
        label: (t: TFunction) => t("attribute_value::Add New Attribute Value"),
      },
    },
  },
  {
    path: "attribute-values/:subId",
    lazy: lazyEntityCrumb(() =>
      import("src/subEntities/attributeValueHooks").then(
        (module) => module.useAttributeValueCrumbLabel,
      ),
    ),
    children: [
      {
        index: true,
        lazy: lazyRoute(() => import("src/subEntities/AttributeValueDetails")),
        // Reading a value is governed by the definitions it belongs to, not by
        // the section it hangs off.
        handle: { access: ACCESS_RULES.dataDefinitionsRead },
      },
      {
        path: "edit",
        lazy: lazyRoute(() => import("src/subEntities/AttributeValueForm"), {
          parentType,
        }),
        handle: {
          access: write,
          crumb: { label: (t: TFunction) => t("common::Edit") },
        },
      },
    ],
  },
];

/**
 * One CRUD section as a nested route: the section names itself, the `:id`
 * parent names the entity, and every page below inherits both. The `:id` parent
 * renders nothing of its own, so the details page stays an index child rather
 * than becoming a layout for the pages beside it.
 *
 * Access rules stay on the pages rather than the section, so a page reached
 * through a section it cannot write to is unaffected.
 */
const addLabel = (t: TFunction) => t("common::Add");
const editLabel = (t: TFunction) => t("common::Edit");

export const buildEntitySection = ({
  access,
  attributeValues,
  attributeValuesWrite,
  configFlag,
  details,
  entityLabel,
  entityChildren = [],
  form,
  icon,
  idParam = "id",
  label,
  list,
  noAddPage = false,
  path,
  sectionChildren = [],
}: EntitySectionOptions): RouteObject => ({
  path,
  handle: { crumb: { icon, label }, configFlag },
  children: [
    { index: true, lazy: list, handle: { access: access?.read } },
    ...sectionChildren,
    ...(form && !noAddPage
      ? [
          {
            path: "add",
            lazy: form,
            handle: { access: access?.write, crumb: { label: addLabel } },
          },
        ]
      : []),
    {
      path: `:${idParam}`,
      lazy: lazyEntityCrumb(entityLabel),
      children: [
        ...(details
          ? [{ index: true, lazy: details, handle: { access: access?.read } }]
          : []),
        ...(form
          ? [
              {
                path: "edit",
                lazy: form,
                handle: {
                  access: access?.write,
                  crumb: { label: editLabel },
                },
              },
            ]
          : []),
        ...(attributeValues
          ? attributeValueRoutes(attributeValues, attributeValuesWrite)
          : []),
        ...entityChildren,
      ],
    },
  ],
});
